import type { BoardKind } from '@/features/board/layout'
import { setBoardKind, useBoardKind } from '@/features/board/useBoardKind'
import { setChatOn, useChatOn } from '@/features/chat/settings'
import { play, setVolume, useVolume } from '@/features/game/sound'
import { setTheme, type Theme, useTheme } from '@/features/game/useTheme'

/**
 * Настройки: тема, громкость, вид поля и чат.
 *
 * Один дом на все настройки, а не карточки, разложенные по экранам. До него
 * тема жила кнопкой в шапке, вид поля — карточкой в лобби, а громкости не
 * было вовсе: каждая настройка оказывалась там, где нашлось место, и найти их
 * вместе было нельзя. Заодно это освободило лобби — оно борется за вертикаль,
 * чтобы начать партию можно было, не прокручивая.
 *
 * Показывается модальным окном поверх экрана (Modal), а не вместо него:
 * громкость крутят посреди партии, и уходить за этим с доски незачем. Адрес
 * при этом свой, /settings, — «назад» браузера окно закрывает.
 *
 * Все они — личные и на партию не влияют: соперник за тем же столом может
 * смотреть на крест в светлой теме, пока вы смотрите на меандр в тёмной.
 * Поэтому и живут они в браузере, а не в учётной записи, и работают у гостя
 * так же, как у вошедшего.
 */
export function SettingsScreen() {
  return (
    <div className="settings">
      <h2 className="hero__title">Настройки</h2>

      <ThemeCard />
      <VolumeCard />
      <BoardKindCard />
      <ChatCard />
    </div>
  )
}

const THEMES: { value: Theme; label: string }[] = [
  { value: 'light', label: 'светлая' },
  { value: 'dark', label: 'тёмная' },
]

function ThemeCard() {
  const theme = useTheme()

  return (
    <div className="card">
      <h2 className="card__title">Тема</h2>
      <div className="field field--split">
        {THEMES.map((option) => (
          <button
            key={option.value}
            className={option.value === theme ? 'primary' : ''}
            type="button"
            aria-pressed={option.value === theme}
            onClick={() => setTheme(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p className="muted">
        Пока выбора не сделали, тема берётся из настроек системы. Выбранная здесь — сильнее её.
      </p>
    </div>
  )
}

/**
 * Громкость — одним ползунком, и ноль на нём означает «выключено».
 *
 * Отдельного выключателя рядом нет намеренно: два состояния на одну вещь
 * пришлось бы сводить друг с другом, и «включить» после приглушения до десяти
 * не знало бы, куда возвращать. Кнопки звука в шапке по этой же причине
 * больше нет — за громкостью теперь приходят сюда, и приходят посреди партии:
 * окно открывается поверх доски.
 */
function VolumeCard() {
  const volume = useVolume()

  /*
   * Звук подаётся на ОТПУСКАНИЕ, а не на каждое движение ползунка.
   *
   * onChange у range приходит на каждый шаг, и голос на каждом превращался бы
   * в стрёкот — ровно то, чего в игре нет и по другим причинам (см. sound.ts).
   * Отпустив ползунок, человек слышит, что выбрал.
   */
  const listen = () => {
    if (volume > 0) play('roll')
  }

  return (
    <div className="card">
      <h2 className="card__title">Громкость</h2>
      <div className="volume">
        <input
          className="volume__slider"
          type="range"
          min={0}
          max={100}
          step={5}
          value={volume}
          aria-label="громкость"
          onChange={(event) => setVolume(Number(event.target.value))}
          onPointerUp={listen}
          onKeyUp={listen}
        />
        <span className="volume__value">{volume === 0 ? 'выкл' : `${volume}%`}</span>
      </div>
      <p className="muted">
        Звук говорит то же, что подсветка и строка под доской, — чей ход, что срубили, что кто-то
        дошёл до дома. Он нужен, когда смотришь в сторону.
      </p>
    </div>
  )
}

/**
 * Чат за столом — выключателем, а не громкостью.
 *
 * Здесь же, где звук и вид поля, и по той же причине: это личная настройка,
 * а не часть партии — соперник за тем же столом может переписываться, пока
 * вы играете молча. Отдельного «заткнуть вот этого» здесь нет: за этим
 * приходят посреди партии, и кнопка стоит в самом чате.
 */
function ChatCard() {
  const on = useChatOn()

  return (
    <div className="card">
      <h2 className="card__title">Чат за столом</h2>
      <div className="field field--split">
        <button
          className={on ? 'primary' : ''}
          type="button"
          aria-pressed={on}
          onClick={() => setChatOn(true)}
        >
          включён
        </button>
        <button
          className={on ? '' : 'primary'}
          type="button"
          aria-pressed={!on}
          onClick={() => setChatOn(false)}
        >
          выключен
        </button>
      </div>
      <p className="muted">
        Разговор живёт вместе с партией и исчезает с ней. За открытым столом, куда сажает быстрая
        игра, говорят только готовыми фразами — там рядом незнакомые люди.
      </p>
    </div>
  )
}

/** Как называется каждый вид и что про него надо знать до партии. */
const KINDS: { value: BoardKind; label: string; hint: string }[] = [
  { value: 'classic', label: 'классика', hint: 'крест, 25 клеток в ширину' },
  { value: 'meander', label: 'меандр', hint: 'кольцо сложено вчетверо, 17 клеток' },
]

/**
 * Вид игрового поля.
 *
 * Меняется и посреди партии тоже: правила от вида не зависят, поэтому
 * переключение просто перерисовывает доску.
 */
function BoardKindCard() {
  const kind = useBoardKind()

  return (
    <div className="card">
      <h2 className="card__title">Вид поля</h2>
      <div className="field field--split">
        {KINDS.map((option) => (
          <button
            key={option.value}
            className={option.value === kind ? 'primary' : ''}
            type="button"
            aria-pressed={option.value === kind}
            onClick={() => setBoardKind(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p className="muted">
        {KINDS.find((option) => option.value === kind)?.hint}. Правила одинаковые: меняется только
        то, как уложены клетки, — на меандре они крупнее, и попадать пальцем проще.
      </p>
    </div>
  )
}
